import React from 'react'
import Panel from '../../../components/Panel'
import type { ReconciliationVnemAllocationSummaryVO } from '../../../apis/types'
import { vnemSeverityColors } from './reconciliation.types'

interface MissingVnemAccount {
    name: string
    utilityAccountNumber: string
}

interface ReconciliationVnemPanelProps {
    vnem: ReconciliationVnemAllocationSummaryVO
    missingAccounts: MissingVnemAccount[]
}

const ReconciliationVnemPanel: React.FC<ReconciliationVnemPanelProps> = ({ vnem, missingAccounts }) => {
    const missingCount = missingAccounts.length
    const colors = vnemSeverityColors[vnem.severity]
    const isUnavailable = vnem.severity === 'unavailable'

    const statusLabel = isUnavailable
        ? 'No VNEM data'
        : vnem.deviationFrom100 <= 1
            ? 'OK'
            : vnem.totalPercent > 100
                ? `Over by ${(vnem.totalPercent - 100).toFixed(2)}%`
                : `Under by ${(100 - vnem.totalPercent).toFixed(2)}%`

    const statusBadgeColor = isUnavailable
        ? 'bg-slate-100 dark:bg-slate-700/50 text-slate-500 dark:text-slate-400 ring-slate-300 dark:ring-slate-600'
        : vnem.severity === 'ok'
            ? 'bg-green-100 dark:bg-green-400/10 text-green-700 dark:text-green-400 ring-green-600/10 dark:ring-green-400/20'
            : vnem.severity === 'warning'
                ? 'bg-yellow-100 dark:bg-yellow-400/10 text-yellow-700 dark:text-yellow-400 ring-yellow-600/10 dark:ring-yellow-400/20'
                : 'bg-red-100 dark:bg-red-400/10 text-red-700 dark:text-red-400 ring-red-600/10 dark:ring-red-400/20'

    return (
        <Panel header="VNEM Allocation Summary" className="mt-4">
            <div className="grid grid-cols-4 gap-4 text-sm">
                <div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">Total allocated</div>
                    <div className={`font-medium ${isUnavailable ? 'text-slate-400' : colors.text}`}>
                        {isUnavailable ? '—' : `${vnem.totalPercent.toFixed(2)}%`}
                    </div>
                </div>
                <div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">Target</div>
                    <div className="font-medium dark:text-slate-300">100.00%</div>
                </div>
                <div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">Status</div>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ring-1 ring-inset ${statusBadgeColor}`}>
                        {statusLabel}
                    </span>
                </div>
                <div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">Missing allocation</div>
                    <div className={`font-medium ${missingCount > 0 ? 'text-red-600 dark:text-red-400' : 'text-slate-700 dark:text-slate-300'}`}>
                        {missingCount > 0 ? `${missingCount} account${missingCount !== 1 ? 's' : ''}` : 'None'}
                    </div>
                </div>
            </div>
            {missingAccounts.length > 0 && (
                <div className="mt-3 text-xs text-slate-500 dark:text-slate-400">
                    Missing: {missingAccounts.map((a) => `${a.name || 'Unknown'} (${a.utilityAccountNumber})`).join(', ')}
                </div>
            )}
        </Panel>
    )
}

export type { MissingVnemAccount }
export default ReconciliationVnemPanel
